import { useMemo } from 'react';
import { MapBounds, PointJob } from './TourDetailsMap';
import { useSelectedPlant } from '@energy-stacks/fleet/feature-business-accounts';
import { mapDefaultProps, offsetPoints } from '@energy-stacks/fleet/shared';
import useSupercluster from 'use-supercluster';
import { EditingTourJob } from '../edit-tour/editingTourJobModel';

export const useJobsMapMarkers = (
  jobs: (EditingTourJob & { tourIndex?: number })[],
  bounds: MapBounds,
  zoom: number,
  category: string
) => {
  const selectedPlant = useSelectedPlant();
  const jobPoints = useMemo(() => {
    const pointJobs: PointJob[] = jobs.map((job) => {
      return {
        coords:
          job.jobType === 'INBOUND'
            ? [
                job.origin?.geoLocation.latitude,
                job.origin?.geoLocation.longitude,
              ]
            : [
                job.destination?.geoLocation.latitude,
                job.destination?.geoLocation.longitude,
              ],
        properties: {
          tourIndex: job.tourIndex ?? -1,
          // Add 1 to job visit order not to have 0 based visit orders displayed on the UI
          visitOrder: Number.isInteger(job.visitOrder)
            ? job.visitOrder + 1
            : -1,
          jobId: job.jobId,
          jobProduct: job.product,
        },
      } as PointJob;
    });

    // Handle the case when there are multiple jobs with the same location
    // Plant is always rendered globally, so we need to offset points that are not plant
    return offsetPoints(pointJobs, {
      coords: [
        selectedPlant?.geoLocation.latitude ?? '0',
        selectedPlant?.geoLocation.longitude ?? '0',
      ],
    });
  }, [
    jobs,
    selectedPlant?.geoLocation.latitude,
    selectedPlant?.geoLocation.longitude,
  ]);

  const { clusters, supercluster } = useSupercluster({
    points: jobPoints.map((point) => ({
      type: 'Feature',
      properties: {
        clusterId: `${point.coords[1]}_${point.coords[0]}`,
        category: category,
        jobVisitOrder: point.properties.visitOrder,
        jobProduct: point.properties.jobProduct,
      },
      geometry: {
        type: 'Point',
        coordinates: [point.coords[1], point.coords[0]],
      },
    })),
    bounds,
    zoom,
    options: { radius: 40, maxZoom: mapDefaultProps.maxZoom },
  });

  return useMemo(
    () => ({ clusters, supercluster, jobPoints }),
    [clusters, jobPoints, supercluster]
  );
};
